import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { var_api } from "../../../constant";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const PNLSummaryCards = () => {
  const [loading, setLoading] = useState(true);
  const [pnlData, setPnlData] = useState({
    warehouse_id: "1",
    total_orders: 34,
    item_revenue: 20110.75,
    business_profit: 2198.5,
    cleaning_charge: 751,
    delivery_charge: 104,
    total_revenue: 17151.39,
    total_expenses: 0,
    net_profit: 2198.5
  });

  // 🔹 State for modals
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [showProfitModal, setShowProfitModal] = useState(false);

  // 🔹 Default dates
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(lastDayOfMonth);

  // 🔹 Prepare chart data
  const revenueChartData = [
    { name: 'Items', value: pnlData.item_revenue, color: '#4CAF50' },
    { name: 'Cleaning', value: pnlData.cleaning_charge, color: '#2196F3' },
    { name: 'Delivery', value: pnlData.delivery_charge, color: '#FF9800' }
  ];

  const profitChartData = [
    { name: 'Total Revenue', value: pnlData.total_revenue, color: '#4CAF50' },
    { name: 'Business Profit', value: pnlData.business_profit, color: '#2196F3' },
    { name: 'Net Profit', value: pnlData.net_profit, color: pnlData.net_profit >= 0 ? '#4CAF50' : '#F44336' }
  ];

  const expensesChartData = [
    { name: 'Expenses', value: pnlData.total_expenses, color: '#F44336' }
  ];

  // 🔹 Calculate percentages
  const calculatePercentages = (data) => {
    const totalRevenue = data.total_revenue || 1;
    return {
      itemsPercentage: ((data.item_revenue / totalRevenue) * 100).toFixed(1),
      cleaningPercentage: ((data.cleaning_charge / totalRevenue) * 100).toFixed(1),
      deliveryPercentage: ((data.delivery_charge / totalRevenue) * 100).toFixed(1),
      profitMargin: ((data.net_profit / totalRevenue) * 100).toFixed(1)
    };
  };

  const percentages = calculatePercentages(pnlData);

  // 🔹 Fetch PNL Data
  const fetchPnlData = async () => {
    setLoading(true);
    try {
      const payload = {
        warehouse_id: localStorage.getItem("warehouse_id"),
        from_date: fromDate.toISOString().split('T')[0],
        to_date: toDate.toISOString().split('T')[0],
      };

      const res = await axios.get(`${var_api}veemart_expenses/pnl-summary`, {
        params: payload,
      });

      if (res.data && res.data.success) {
        setPnlData(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching PNL data:", error);
      toast.error("Error fetching PNL data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPnlData();
  }, [fromDate, toDate]);

  // 🔹 Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <>
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Loading Financial Summary...</div>
        </div>
      )}

      {/* Date Range Selector */}
     <Card className="shadow-sm mb-4">
  <Card.Body>
    <Row className="align-items-center">
      <Col md={12} className="mb-3">
        <div className="fw-bold fs-5">
          <i className="bi bi-calendar-range me-2"></i>
          Financial Summary Period
        </div>
        <small className="text-muted">Select date range to analyze revenue, expenses, and profits</small>
      </Col>
      <Col md={3} className="mb-2 mb-md-0">
        <label className="form-label fw-medium">
          <i className="bi bi-calendar-check me-1"></i>
          Start Date
        </label>
        <DatePicker
          className="form-control"
          selected={fromDate}
          onChange={(date) => setFromDate(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Select start date"
        />
      </Col>
    
      <Col md={3}>
        <label className="form-label fw-medium">
          <i className="bi bi-calendar-check me-1"></i>
          End Date
        </label>
        <DatePicker
          className="form-control"
          selected={toDate}
          onChange={(date) => setToDate(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Select end date"
          minDate={fromDate}
        />
      </Col>

        <Col md={4} className="d-none d-md-block">
        <div className="mt-4">
          <i className="bi bi-arrow-right text-muted"></i>
        </div>
      </Col>
    </Row>
  </Card.Body>
</Card>

      {/* Three Main Cards */}
      <Row className="mb-4">
        {/* REVENUE CARD */}
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '5px solid #4CAF50' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">TOTAL REVENUE</h6>
                  <h2 className="mb-0 text-success">{formatCurrency(pnlData.total_revenue)}</h2>
                  <small className="text-muted">From {pnlData.total_orders} orders</small>
                </div>
                <Button 
                  variant="outline-success" 
                  size="sm" 
                  onClick={() => setShowRevenueModal(true)}
                  className="rounded-circle"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="bi bi-info-lg"></i>
                </Button>
              </div>
              
              {/* Revenue Bar Chart */}
              <div className="mt-4">
                <h6 className="text-muted mb-2">Revenue Breakdown</h6>
                <div style={{ height: '150px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={revenueChartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis 
                        fontSize={12}
                        tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Amount']}
                      />
                      <Bar 
                        dataKey="value" 
                        radius={[4, 4, 0, 0]}
                      >
                        {revenueChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="row text-center mt-2">
                  <div className="col-4">
                    <small className="d-block text-muted">Items</small>
                    <small className="fw-bold">{percentages.itemsPercentage}%</small>
                  </div>
                  <div className="col-4">
                    <small className="d-block text-muted">Cleaning</small>
                    <small className="fw-bold">{percentages.cleaningPercentage}%</small>
                  </div>
                  <div className="col-4">
                    <small className="d-block text-muted">Delivery</small>
                    <small className="fw-bold">{percentages.deliveryPercentage}%</small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* EXPENSES CARD */}
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '5px solid #F44336' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">TOTAL EXPENSES</h6>
                  <h2 className="mb-0 text-danger">{formatCurrency(pnlData.total_expenses)}</h2>
                  <small className="text-muted">Costs & Deductions</small>
                </div>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  onClick={() => setShowExpensesModal(true)}
                  className="rounded-circle"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="bi bi-info-lg"></i>
                </Button>
              </div>
              
              {/* Expenses Bar Chart */}
              <div className="mt-4">
                <h6 className="text-muted mb-2">Expenses Overview</h6>
                <div style={{ height: '150px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={expensesChartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis 
                        fontSize={12}
                        tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Amount']}
                      />
                      <Bar 
                        dataKey="value" 
                        radius={[4, 4, 0, 0]}
                        fill="#F44336"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-3">
                  <div className="d-flex justify-content-center align-items-center">
                    <div className="me-3">
                      <div className="progress" style={{ width: '100px', height: '8px' }}>
                        <div 
                          className="progress-bar bg-danger" 
                          style={{ 
                            width: `${Math.min((pnlData.total_expenses / pnlData.total_revenue) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <small className="text-muted">
                        {((pnlData.total_expenses / pnlData.total_revenue) * 100).toFixed(1)}% of Revenue
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* NET PROFIT CARD */}
        <Col md={4}>
          <Card className={`shadow-sm border-0 h-100 ${pnlData.net_profit >= 0 ? 'border-success' : 'border-danger'}`}
                style={{ borderLeft: `5px solid ${pnlData.net_profit >= 0 ? '#4CAF50' : '#F44336'}` }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">NET PROFIT</h6>
                  <h2 className={`mb-0 ${pnlData.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(pnlData.net_profit)}
                  </h2>
                  <small className="text-muted">{percentages.profitMargin}% Profit Margin</small>
                </div>
                <Button 
                  variant={pnlData.net_profit >= 0 ? "outline-success" : "outline-danger"} 
                  size="sm" 
                  onClick={() => setShowProfitModal(true)}
                  className="rounded-circle"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="bi bi-info-lg"></i>
                </Button>
              </div>
              
              {/* Profit Bar Chart */}
              <div className="mt-4">
                <h6 className="text-muted mb-2">Profit Analysis</h6>
                <div style={{ height: '150px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={profitChartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" fontSize={10} />
                      <YAxis 
                        fontSize={10}
                        tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Amount']}
                      />
                      <Bar 
                        dataKey="value" 
                        radius={[4, 4, 0, 0]}
                      >
                        {profitChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="row text-center mt-2">
                  <div className="col-4">
                    <small className="d-block text-muted">Revenue</small>
                    <small className="fw-bold text-success">
                      {formatCurrency(pnlData.total_revenue)}
                    </small>
                  </div>
                  <div className="col-4">
                    <small className="d-block text-muted">Expenses</small>
                    <small className="fw-bold text-danger">
                      {formatCurrency(pnlData.total_expenses)}
                    </small>
                  </div>
                  <div className="col-4">
                    <small className="d-block text-muted">Net</small>
                    <small className={`fw-bold ${pnlData.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(pnlData.net_profit)}
                    </small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Revenue Details Modal */}
      <Modal show={showRevenueModal} onHide={() => setShowRevenueModal(false)} size="lg">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-cash-stack me-2"></i>
            Revenue Breakdown Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-success">
                <tr>
                  <th>Source</th>
                  <th className="text-end">Amount (₹)</th>
                  <th className="text-end">Percentage</th>
                  <th className="text-end">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <i className="bi bi-box me-2"></i>
                    Items Revenue
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(pnlData.item_revenue)}</td>
                  <td className="text-end">{percentages.itemsPercentage}%</td>
                  <td className="text-end">
                    <small className="text-muted">
                      Business Profit: {formatCurrency(pnlData.business_profit)}
                    </small>
                  </td>
                </tr>
                <tr>
                  <td>
                    <i className="bi bi-bucket me-2"></i>
                    Cleaning Charge
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(pnlData.cleaning_charge)}</td>
                  <td className="text-end">{percentages.cleaningPercentage}%</td>
                  <td className="text-end">
                    <small className="text-muted">Service Charge</small>
                  </td>
                </tr>
                <tr>
                  <td>
                    <i className="bi bi-truck me-2"></i>
                    Delivery Charge
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(pnlData.delivery_charge)}</td>
                  <td className="text-end">{percentages.deliveryPercentage}%</td>
                  <td className="text-end">
                    <small className="text-muted">Delivery Service</small>
                  </td>
                </tr>
                <tr className="table-success">
                  <td>
                    <i className="bi bi-calculator me-2"></i>
                    <strong>Total Revenue</strong>
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(pnlData.total_revenue)}</td>
                  <td className="text-end fw-bold">100%</td>
                  <td className="text-end">
                    <small className="text-muted">
                      {pnlData.total_orders} Orders
                    </small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Revenue Pie Chart */}
          <div className="mt-4" style={{ height: '250px' }}>
            <h6 className="mb-3">Revenue Distribution</h6>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  innerRadius={40}
                  dataKey="value"
                >
                  {revenueChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRevenueModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Expenses Details Modal */}
      <Modal show={showExpensesModal} onHide={() => setShowExpensesModal(false)} size="lg">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-cash-coin me-2"></i>
            Expenses Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Current expenses data shows ₹0. Please ensure expenses are properly recorded.
          </div>
          
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-danger">
                <tr>
                  <th>Expense Category</th>
                  <th className="text-end">Amount (₹)</th>
                  <th className="text-end">Impact on Revenue</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Expenses</td>
                  <td className="text-end fw-bold text-danger">{formatCurrency(pnlData.total_expenses)}</td>
                  <td className="text-end">
                    {((pnlData.total_expenses / pnlData.total_revenue) * 100).toFixed(1)}% of Revenue
                  </td>
                </tr>
                <tr className="table-danger">
                  <td>
                    <strong>Remaining for Profit</strong>
                  </td>
                  <td className="text-end fw-bold">
                    {formatCurrency(pnlData.total_revenue - pnlData.total_expenses)}
                  </td>
                  <td className="text-end">
                    {100 - ((pnlData.total_expenses / pnlData.total_revenue) * 100).toFixed(1)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Expenses Bar Chart */}
          <div className="mt-4" style={{ height: '200px' }}>
            <h6 className="mb-3">Revenue vs Expenses</h6>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'Revenue', value: pnlData.total_revenue, color: '#4CAF50' },
                  { name: 'Expenses', value: pnlData.total_expenses, color: '#F44336' },
                  { name: 'Remaining', value: pnlData.total_revenue - pnlData.total_expenses, color: '#2196F3' }
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                <Bar dataKey="value">
                  {[
                    { name: 'Revenue', value: pnlData.total_revenue, color: '#4CAF50' },
                    { name: 'Expenses', value: pnlData.total_expenses, color: '#F44336' },
                    { name: 'Remaining', value: pnlData.total_revenue - pnlData.total_expenses, color: '#2196F3' }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExpensesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Profit Details Modal */}
      <Modal show={showProfitModal} onHide={() => setShowProfitModal(false)} size="lg">
        <Modal.Header closeButton className={pnlData.net_profit >= 0 ? "bg-success text-white" : "bg-danger text-white"}>
          <Modal.Title>
            <i className={`bi ${pnlData.net_profit >= 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow'} me-2`}></i>
            Profit Analysis Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className={pnlData.net_profit >= 0 ? "table-success" : "table-danger"}>
                <tr>
                  <th>Calculation</th>
                  <th className="text-end">Amount (₹)</th>
                  <th className="text-end">Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Revenue</td>
                  <td className="text-end">{formatCurrency(pnlData.total_revenue)}</td>
                  <td className="text-end">100%</td>
                </tr>
                <tr>
                  <td className="ps-4">
                    <i className="bi bi-dash-circle me-2 text-danger"></i>
                    Total Expenses
                  </td>
                  <td className="text-end text-danger">-{formatCurrency(pnlData.total_expenses)}</td>
                  <td className="text-end text-danger">
                    -{((pnlData.total_expenses / pnlData.total_revenue) * 100).toFixed(1)}%
                  </td>
                </tr>
                <tr className={pnlData.net_profit >= 0 ? "table-success" : "table-danger"}>
                  <td>
                    <strong>NET PROFIT</strong>
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(pnlData.net_profit)}</td>
                  <td className="text-end fw-bold">{percentages.profitMargin}%</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Profit Breakdown Chart */}
          <div className="mt-4" style={{ height: '250px' }}>
            <h6 className="mb-3">Profit Calculation Flow</h6>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { 
                    name: 'Revenue', 
                    value: pnlData.total_revenue, 
                    fill: '#4CAF50'
                  },
                  { 
                    name: 'Expenses', 
                    value: pnlData.total_expenses, 
                    fill: '#F44336'
                  },
                  { 
                    name: 'Net Profit', 
                    value: Math.abs(pnlData.net_profit), 
                    fill: pnlData.net_profit >= 0 ? '#4CAF50' : '#F44336'
                  }
                ]}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Profit Summary */}
          <div className="mt-4 p-3 bg-light rounded">
            <h6>Profit Summary</h6>
            <Row>
              <Col md={6}>
                <div className="mb-2">
                  <small className="text-muted d-block">Profit per Order</small>
                  <strong>{formatCurrency(pnlData.net_profit / (pnlData.total_orders || 1))}</strong>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <small className="text-muted d-block">Business Profit Margin</small>
                  <strong>
                    {((pnlData.business_profit / pnlData.item_revenue) * 100).toFixed(1)}%
                  </strong>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProfitModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx="true">{`
        .card {
          transition: transform 0.2s;
        }
        .card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
        }
        .progress {
          background-color: #e9ecef;
        }
        .progress-bar {
          transition: width 0.6s ease;
        }
      `}</style>

      <ToastContainer />
    </>
  );
};

export default PNLSummaryCards;